import {
  Component,
  createRef,
  useContext,
  useEffect,
  useState,
} from "react";
import { lazyWithRetry } from "../utils/lazyWithRetry";

import "@aws-amplify/ui/dist/style.css";
import { Button, styled, useMediaQuery, useTheme } from "@mui/material";
import * as Sentry from "@sentry/react";
import Amplify from "aws-amplify";
import { Authenticator, Greetings, Loading, SignIn } from "aws-amplify-react";
import { AxiosResponse } from "axios";
import { MaterialDesignContent, SnackbarProvider } from "notistack";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { PageTitleProvider, usePageTitle } from "./PageTitle";
import { useDisplayAlias } from "../hub/hubLabel";

import { axios } from "../api";
import AppLayout from "../components/AppBackground";
import SectionHomeLayout from "../components/sectionDashboard/SectionHomeLayout";
import CustomLoading, { hideLoader } from "../components/AppLoading";
import { FeatureContext } from "./Features";
import CustomSignIn from "./SignIn";

import type { AeronetV6Response } from "../types";
import type { IAeronetNav } from "./types";
import { applyNavModeChildren } from "./navModeRegistry";
import GcipGate from "../auth/GcipGate";
import SignPage from "../sign/SignPage";
import WakeGate from "../components/WakeGate";
import { StepUpHost } from "../components/StepUpDialog";
import { getSessionUser } from "../auth/session";
import { getAuthProvider, getCognitoConfig, isPureV6Tenant } from "../config/product";
import {
  dropOffTenantLegacyItems,
  filterNavForTenant,
  loadLegacyHrefAllow,
  MOBILE_EMULATOR_FUNCTION,
  mobileEmulatorTarget,
  sanitizeMaintenanceNav,
} from "./navVisibility";
import { signOut as gcipSignOut } from "../auth/gcip";
import { takeReturnTo } from "../auth/authRecovery";
import Invoices from "../invoices/Invoices";
import Components from "../component/Component";
import Inspections from "../inspections/Inspections";
import Reports from "../reports/Reports";
import DueDate from "../DueDate/DueDate";
import { LoadingView } from "../components/Loading";
import { useCurrentUser } from "../hooks/useCurrentUser";
import MetaBaseDashboard from "../dashboard/metabase/MetabaseDashbord";
import MetabaseRoutes from "../routes/MetabaseRoutes";
import { navRequestConfig } from "./navRequest";
import { recordSignIn } from "./signInWrites";


const Dashboard = lazyWithRetry(() => import("../dashboard/Dashboard"));
const InspectionsTR = lazyWithRetry(() => import("../inspections-tr"));
const NotFound = lazyWithRetry(() => import("./NotFound"));
const Settings = lazyWithRetry(() => import("./Settings"));
const Tools = lazyWithRetry(() => import("../tools/Tools"));
const AircraftServices = lazyWithRetry(() => import("../aircraft-services"));
const ToolRequests = lazyWithRetry(() => import("../tool-requests"));
const Tasks = lazyWithRetry(() => import("../tasks"));
const Sors = lazyWithRetry(() => import("../sors/Sors"));
const Rmas = lazyWithRetry(() => import("../rmas/Rmas"));
const Aircraft = lazyWithRetry(() => import("../aircraft/Aircraft"));
const Fleet = lazyWithRetry(() => import("../fleet"));
const Documents = lazyWithRetry(() => import("../documents/Documents"));
const PartsOrders = lazyWithRetry(()=> import("../parts-orders/Orders"))
const Timesheets = lazyWithRetry(() => import("../timesheets"));
const Production = lazyWithRetry(() => import("../production"));
const PartsRequisition = lazyWithRetry(() => import("../parts-requisition"));
const ActionRegister = lazyWithRetry(()=> import("../action_register/ActionRegister"))
const SalesOrder = lazyWithRetry(()=> import("../salesorder/SalesOrderList"))
const WorkOrder = lazyWithRetry(() => import("../workorder/WorkOrderList"))
const Jobs = lazyWithRetry(() => import("../jobs/JobList"));
const FollowUp = lazyWithRetry(()=> import("../followups/FollowUp"))
const Quotes = lazyWithRetry(() => import("../quotes/Quotes"));
const FinancialReports = lazyWithRetry(() => import("../financial-reports"));
const Freight = lazyWithRetry(() => import("../freight"));
const Groups = lazyWithRetry(() => import("../groups"));
const Currency = lazyWithRetry(() => import("../currency/index"));
const Certificates = lazyWithRetry(() => import("../certificates"));
const Orders = lazyWithRetry(() => import("../orders/Orders"));
const ReportsAndForms = lazyWithRetry(() => import("../reports-and-forms"));
const ServiceOrders = lazyWithRetry(() => import("../service-orders"));
const InwardsGoods = lazyWithRetry(() => import("../inwards-goods"));
const MaintenanceLog = lazyWithRetry(() => import("../maintenance-log"));
const Modifications = lazyWithRetry(() => import("../modifications"));
const RoleEquipment = lazyWithRetry(() => import("../role-equipment"));
const TechLog = lazyWithRetry(() => import("../tech-log"));
const Trend = lazyWithRetry(() => import("../trend"));
const SystemConfig = lazyWithRetry(() => import("../system-config"));
const SystemReports = lazyWithRetry(() => import("../system-reports"));
const Personnel = lazyWithRetry(() => import("../personnel"));
const History = lazyWithRetry(() => import("../history"));
const Manifest = lazyWithRetry(() => import("../manifest"));
const Companies = lazyWithRetry(() => import("../companies/CompanyList"));
const SupplierInvoices = lazyWithRetry(() => import("../supplier-invoices/SupplierInvoices"));
// Accounts Exports (H255) — static route so /app/xero/batches' redirect lands
// before (and without) the nav row; the nav row routes here too.
const BatchExports = lazyWithRetry(() => import("../batchexports"));


const HelpChat = lazyWithRetry(() => import("../help-chat/HelpChat"));
const ConfluenceHelpCenter = lazyWithRetry(() => import("../help/ConfluenceHelpCenter"));
const LandingPage = lazyWithRetry(() => import("../help/LandingPage"));
const Hub = lazyWithRetry(() => import("../hub/index"));
const ReleaseNotesCenter = lazyWithRetry(() => import("../release-notes/ReleaseNotesCenter"));
const RoadmapCenter = lazyWithRetry(() => import("../roadmap/RoadmapCenter"));
const TicketPortal = lazyWithRetry(() => import("../ticket-portal/TicketPortal"));
const FeatureRequestsPortal = lazyWithRetry(() => import("../feature-requests/TicketPortal"));
// const Onboarding = lazy(() => import("../onboarding/Onboarding"));

const DirectoryDashboard = lazyWithRetry(() => import("../directory/DirectoryDashboard"));
const SalesDashboard = lazyWithRetry(() => import("../sections/SalesDashboard"));
const PurchasingDashboard = lazyWithRetry(() => import("../sections/PurchasingDashboard"));
const TechRecordsDashboard = lazyWithRetry(() => import("../sections/TechRecordsDashboard"));
const FlightAdminDashboard = lazyWithRetry(() => import("../sections/FlightAdminDashboard"));
const MaintenanceDashboard = lazyWithRetry(() => import("../sections/MaintenanceDashboard"));
const StoresDashboard = lazyWithRetry(() => import("../sections/StoresDashboard"));
const FinanceDashboard = lazyWithRetry(() => import("../sections/FinanceDashboard"));
const Journals = lazyWithRetry(() => import("../journals/Journals"));
const SafetyQualityDashboard = lazyWithRetry(() => import("../sections/SafetyQualityDashboard"));
const SystemAdminDashboard = lazyWithRetry(() => import("../sections/SystemAdminDashboard"));
const PilotsDashboard = lazyWithRetry(() => import("../sections/PilotsDashboard"));
const Weather = lazyWithRetry(() => import("../weather"));
const XeroCallback = lazyWithRetry(() => import("../xero/XeroCallback"));
const XeroSettings = lazyWithRetry(() => import("../xero/XeroSettings"));
const Transpond = lazyWithRetry(() => import("../transpond"));
const Microsoft365 = lazyWithRetry(() => import("../integrations/Microsoft365"));
const Sage300 = lazyWithRetry(() => import("../integrations/Sage300"));
const Laserfiche = lazyWithRetry(() => import("../integrations/Laserfiche"));
const ModuleLandingPage = lazyWithRetry(() => import("../components/ModuleLandingPage"));
const MobileEmulator = lazyWithRetry(() => import("../mobile-emulator/MobileEmulator"));

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const TitleSetter: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => {
  const { setTitle } = usePageTitle();
  useEffect(() => {
    setTitle(title);
    return () => setTitle(null);
  }, [title, setTitle]);
  return <>{children}</>;
};

// Cognito config is resolved PER TENANT at runtime from the request Host
// (src/config/product.ts) — never from build-time env: one image serves every
// tenant, so a baked pool/client would be wrong for all but one of them.
// GCIP tenants are configured lazily by src/auth instead.
if (getAuthProvider() === "cognito") {
  const cognito = getCognitoConfig();
  Amplify.configure({
    Auth: {
      identityPoolId: cognito.identityPoolId,
      region: cognito.region,
      userPoolId: cognito.userPoolId,
      userPoolWebClientId: cognito.userPoolWebClientId,
      oauth: {
        domain: cognito.domain,
        scope: [
          "phone",
          "email",
          "profile",
          "openid",
          "aws.cognito.signin.user.admin",
        ],
        redirectSignIn: cognito.redirectSignIn,
        redirectSignOut: cognito.redirectSignOut,
        responseType: "code", // or 'token', note that REFRESH token will only be generated when the responseType is code
      },
    },
  });
}

/** Redirects legacy /help-center/:pageId to canonical /aeronet-help/:pageId preserving the id. */
const HelpCenterPageRedirect: React.FC = () => {
  const { pageId = '' } = useParams<{ pageId: string }>();
  return <Navigate to={`/aeronet-help/${pageId}`} replace />;
};

/** Redirects legacy /help-v2/:pageId to canonical /hub/:pageId preserving the id. */
const HubPageRedirect: React.FC = () => {
  const { pageId = '' } = useParams<{ pageId: string }>();
  return <Navigate to={`/hub/${pageId}`} replace />;
};

/** Generic landing stub for top-nav sections without a built dashboard —
 *  the dropdown home item always lands somewhere (Menu.tsx falls back here). */
const SectionLanding: React.FC = () => {
  const { sectionName = "" } = useParams<{ sectionName: string }>();
  const title = decodeURIComponent(sectionName) || "Home";
  return (
    <TitleSetter title={title}>
      <ModuleLandingPage title={title} />
    </TitleSetter>
  );
};

interface AppProps {
  authState?: string;
  authData?: any;
  hideLoader?: () => void;
}

// NO STATIC FALLBACK NAV (removed 2026-10-14). DEFAULT_G2_NAV, a hand-typed
// copy of g2's menu, was installed on a failed /v6/nav read on g2 only — a
// product gate (banned, blank/PATTERNS.md), and on any other tenant the wrong
// menu (aquipe got it on 2026-08-21). Every tenant now keeps an empty nav and
// surfaces the failure.

const App: React.FC<AppProps> = ({ authData, authState, hideLoader }) => {
  const [nav, setNav] = useState<readonly IAeronetNav[]>([]);
  const { isFeatureActive, getFeature } = useContext(FeatureContext);

  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const currentUser = useCurrentUser();
  const displayAlias = useDisplayAlias();

  // Sign-in bookkeeping — last_login and Invited → Active — decided by the
  // server on POST /v6/users/me/signed-in, once per browser session, quietly
  // (H201; see src/app/signInWrites.ts).
  useEffect(() => {
    if (authState !== "signedIn" || !currentUser?.id) return;
    recordSignIn(currentUser);
  }, [authState, currentUser]);

  // Back to the page the user was on when their session ran out (or when a
  // deep link sent them to sign in): the login round trip lands on /app/.
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (authState !== "signedIn") return;
    const returnTo = takeReturnTo();
    if (returnTo && returnTo !== `${location.pathname}${location.search}${location.hash}`) {
      navigate(returnTo, { replace: true });
    }
    // Once per sign-in, not on every navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  useEffect(() => {
    document.title = `Aeronet - ${displayAlias || "Aeronet"}`;
  }, [displayAlias]);
  
  if (hideLoader) {
    hideLoader();
  }

  useEffect(() => {
    let active = true;

    async function setUpNav() {
      let cognitoUser: any = null;
      try {
        cognitoUser = await getSessionUser();
        if (cognitoUser?.attributes) {
          Sentry.setUser({
            id: cognitoUser.attributes["custom:aeronet_user_id"],
            username: cognitoUser.username,
            email: cognitoUser.attributes.email,
            ...cognitoUser.attributes,
          });
        }
      } catch (err) {
        // Local dev or unauthenticated session — proceed with nav fetch
      }

      let navUrl = "/v6/nav";
      const userId = cognitoUser?.attributes?.["custom:aeronet_user_id"];
      if (
        userId &&
        process.env.NODE_ENV === "development" &&
        process.env.REACT_APP_API_URL === "http://localhost:9999"
      ) {
        // call api with user id when running locally as the api doesn't get the jwt decoded by api gateway
        navUrl = navUrl + "/" + userId;
        console.warn("Using alternate get_nav_by_id api");
      }

      // Read beside the nav request, not after it. It never rejects.
      const legacyHrefAllow = loadLegacyHrefAllow();

      try {
        const navUserId = cognitoUser?.attributes?.["custom:aeronet_user_id"] ?? "local";
        const resp: AxiosResponse<AeronetV6Response<IAeronetNav>> =
          // Shared with useSectionReachability (app/navRequest.ts): one fetch.
          await axios.get(navUrl, navRequestConfig(navUserId));

        // this sets up the paths to React views for features that are active
        // the default is that the React view uses the same path as the function name in Roxen
        // this is the place to override that
        for (const navItem of resp.data.data) {
          for (const menuItem of navItem.children) {
            if (menuItem.function !== "url" && menuItem.function !== "custom") {
              if (isFeatureActive(menuItem.function)) {
                switch (menuItem.function) {
                  case "users_prefs":
                    menuItem.to = "preferences";
                    break;
                  case "module_stationery":
                    menuItem.to = "reports-and-forms";
                    menuItem.name = "Reports and Forms";
                    break;
                  case "jobs":
                  case "quotes":
                  case "documents":
                  case "freight":
                  case "groups":
                  case "currency":
                  case "certs":
                  case "orders":
                  case "tds":
                  case "inwards":
                  case "job_costs":
                    // Cognito products on legacy installs (scm/crm/ops/mt/mro) keep the legacy
                    // Roxen href for these. Pure v6 tenants (g2, aquipe, demo, etc.) have NO
                    // legacy install — every menu item lands on its v6 module (Aaron 2026-08-22:
                    // "all should be app/").
                    if (isPureV6Tenant()) {
                      menuItem.to = menuItem.function;
                    }
                    break;
                  case "system_config":
                  case "system-config":
                  case "systemconfig":
                  case "types":
                    menuItem.to = "system-config";
                    menuItem.name = "System Config";
                    break;
                  case "reports":
                  case "system_reports":
                  case "system-reports":
                    menuItem.to = "reports";
                    menuItem.name = "Reports";
                    break;
                  case "service_requests":
                  case "toolrequests":
                    menuItem.to = "toolrequests";
                    menuItem.name = "Tool Requests";
                    break;
                  case MOBILE_EMULATOR_FUNCTION:
                    // The row's url_query picks the emulator view (M4).
                    menuItem.to = mobileEmulatorTarget(menuItem.url);
                    break;
                  default:
                    menuItem.to = menuItem.function;
                }
                applyNavModeChildren(menuItem, menuItem.function);
              }
            } else {
              if (isFeatureActive(menuItem.remote_menu_key)) {
                switch (menuItem.remote_menu_key) {
                  case "module_stationery":
                    menuItem.to = "reports-and-forms";
                    menuItem.name = "Reports and Forms";
                    break;
                  case "financereports":
                    // Land on the dashboard directly — matches legacy
                    // /app/financereports/dashboard (Aaron 2026-08-22).
                    menuItem.to = "financereports/dashboard";
                    break;
                  case "system_config":
                  case "system-config":
                  case "systemconfig":
                  case "types":
                    menuItem.to = "system-config";
                    menuItem.name = "System Config";
                    break;
                  case "reports":
                  case "system_reports":
                  case "system-reports":
                    menuItem.to = "reports";
                    menuItem.name = "Reports";
                    break;
                  case "service_requests":
                  case "toolrequests":
                    menuItem.to = "toolrequests";
                    menuItem.name = "Tool Requests";
                    break;
                  default:
                    menuItem.to = menuItem.remote_menu_key;
                }
                // Generic: subMenus for any mode-bearing module come from the
                // module's OWN mode list (src/app/navModeRegistry.ts), not a
                // hand-typed copy per case above — that hand-typed copy is
                // what let Inventory's nav list drift out of sync with
                // InventoryLayout's real modes. A miss here is a no-op.
                applyNavModeChildren(menuItem, menuItem.remote_menu_key);
              }
            }
          }
        }
        const allow = await legacyHrefAllow;
        if (active) {
          // Tenant shaping (src/app/navVisibility.ts). The legacy-href drop runs
          // last, on the `to` values the loop above has just set.
          const navData = dropOffTenantLegacyItems(
            sanitizeMaintenanceNav(filterNavForTenant(resp.data.data || [])),
            allow
          );
          setNav(navData);
        }
      } catch (e: any) {
        console.error("Error setting up nav:", e);
        if (getAuthProvider() === "gcip" && e?.response?.status === 401) {
          gcipSignOut();
          window.location.reload();
          return;
        }
        // No fallback menu: an empty nav and the logged failure, on every
        // tenant (see the note where DEFAULT_G2_NAV used to be).
        return;
      }
    }

    // GcipGate and the local-dev bypass both mount <App authState="signedIn">,
    // so this single check covers Cognito, GCIP and local. Do NOT widen it with
    // env-var sniffing: the unified image bakes no REACT_APP_COGNITO_* vars, so
    // "pool id missing" is true in production and ran nav setup pre-login.
    if (authState === "signedIn") {
      setUpNav();
    }

    return () => {
      active = false;
    };
  }, [authState, isFeatureActive]);

  return (
    <PageTitleProvider>
      {/* Cold-start explanation + "Keep awake 1h/2h/4h", only after sign-in (src/app/wake.ts). */}
      <WakeGate signedIn={authState === "signedIn"} />
      {/* The sign-off password check the API asks for with a 428 (src/auth/stepUp.ts). */}
      {authState === "signedIn" ? <StepUpHost /> : null}
      <SentryRoutes>
        <Route element={<AppLayout nav={nav} />}>
        {nav.length > 0 ?  <Route path="/" element={<TitleSetter title="Dashboard"><Dashboard /></TitleSetter>} /> : <Route path="/" element={<TitleSetter title="Loading"><LoadingView /></TitleSetter>} />}

     
        <Route path="help-chat/*" element={<HelpChat />} />

        {/* Canonical Help Center */}
        <Route path="aeronet-help/*" element={<LandingPage />} />
        <Route path="aeronet-help/:pageId" element={<ConfluenceHelpCenter />} />
        {/* Legacy aliases → canonical (kept for in-content links & bookmarks) */}
        <Route path="help-center/:pageId" element={<HelpCenterPageRedirect />} />
        <Route path="help-center/*" element={<Navigate to="/aeronet-help" replace />} />
        <Route path="help/*" element={<Navigate to="/aeronet-help" replace />} />

        <Route path="hub/:pageId" element={<Hub />} />
        <Route path="hub/*" element={<Hub />} />
        {/* Legacy alias → canonical Hub (kept for in-content links & bookmarks) */}
        <Route path="help-v2/:pageId" element={<HubPageRedirect />} />
        <Route path="help-v2/*" element={<Navigate to="/hub" replace />} />
        {/* Chat citations deep-link to the API/space path /app/aeronet-sops/<id>
            (the SOP space name). The SOP UI lives at /hub, so map it across —
            also covers citation links already persisted in users' localStorage. */}
        <Route path="aeronet-sops/:pageId" element={<HubPageRedirect />} />
        <Route path="aeronet-sops/*" element={<Navigate to="/hub" replace />} />

        {/* Canonical Release Notes */}
        <Route path="aeronet-release-notes/*" element={<ReleaseNotesCenter />} />
        <Route path="aeronet-release-notes/:pageId" element={<ReleaseNotesCenter />} />
        {/* Legacy alias → canonical */}
        <Route path="release-notes/*" element={<Navigate to="/aeronet-release-notes" replace />} />
        <Route path="release-notes/:pageId" element={<ReleaseNotesCenter />} />

        <Route path="aeronet-roadmap/*" element={<RoadmapCenter />} />
        <Route path="aeronet-roadmap/:pageId" element={<RoadmapCenter />} />

        <Route path="ticket-portal/*" element={<TicketPortal />} />
        <Route path="feature-requests/*" element={<FeatureRequestsPortal />} />
        <Route path="mobile-emulator/*" element={<TitleSetter title="Mobile Emulator"><MobileEmulator /></TitleSetter>} />
        <Route path="mobile/*" element={<TitleSetter title="Mobile Emulator"><MobileEmulator /></TitleSetter>} />

        {/* Section Landing Dashboards — SectionHomeLayout renders the shell
            breadcrumb ("<Module>" crumb, no mode dropdown: these are
            tab-based homes, not mode-switched modules); SectionDashboard
            itself no longer renders a ListingHeader (WP5b cleanup). */}
        <Route path="directory/*" element={<TitleSetter title="Directory"><SectionHomeLayout title="Directory"><DirectoryDashboard originalUrl="" title="Directory" /></SectionHomeLayout></TitleSetter>} />
        <Route path="sales/*" element={<TitleSetter title="Sales"><SectionHomeLayout title="Sales"><SalesDashboard originalUrl="" title="Sales" /></SectionHomeLayout></TitleSetter>} />
        <Route path="purchasing/*" element={<TitleSetter title="Purchasing"><SectionHomeLayout title="Purchasing"><PurchasingDashboard originalUrl="" title="Purchasing" /></SectionHomeLayout></TitleSetter>} />
        <Route path="tech-records/*" element={<TitleSetter title="Tech Records"><SectionHomeLayout title="Tech Records"><TechRecordsDashboard originalUrl="" title="Tech Records" /></SectionHomeLayout></TitleSetter>} />
        <Route path="maintenance/*" element={<TitleSetter title="Maintenance"><SectionHomeLayout title="Maintenance"><MaintenanceDashboard originalUrl="" title="Maintenance" /></SectionHomeLayout></TitleSetter>} />
        <Route path="stores/*" element={<TitleSetter title="Stores"><SectionHomeLayout title="Stores"><StoresDashboard originalUrl="" title="Stores" /></SectionHomeLayout></TitleSetter>} />
        <Route path="finance/*" element={<TitleSetter title="Finance"><SectionHomeLayout title="Finance"><FinanceDashboard originalUrl="" title="Finance" /></SectionHomeLayout></TitleSetter>} />
        <Route path="safety-quality/*" element={<TitleSetter title="Safety & Quality"><SectionHomeLayout title="Safety & Quality"><SafetyQualityDashboard originalUrl="" title="Safety & Quality" /></SectionHomeLayout></TitleSetter>} />
        <Route path="system-admin/*" element={<TitleSetter title="System Admin"><SectionHomeLayout title="System Admin"><SystemAdminDashboard originalUrl="" title="System Admin" /></SectionHomeLayout></TitleSetter>} />
        {/* Pilots. Same shape as the eight above, and deliberately at slug
            "pilots" — NOT "landing/Pilots", which is Menu.tsx's fallback for a
            section with no built home. Pilots and Flight Admin are the same
            menu separated by permission (an admin sees all flights, a pilot
            sees their own), so this route intentionally reuses the shared
            module routes rather than opening a parallel /pilots/* tree. */}
        <Route path="pilots/*" element={<TitleSetter title="Pilots"><SectionHomeLayout title="Pilots"><PilotsDashboard originalUrl="" title="Pilots" /></SectionHomeLayout></TitleSetter>} />
        {/* Flight Admin. Same rule as Pilots above: slug "flight-admin", never
            "landing/Flight Admin". Its destinations are gated on the live nav
            (SectionConfig.gateOnNav) because most of this menu is built and
            endpoint-backed but provisioned per tenant. */}
        <Route path="flight-admin/*" element={<TitleSetter title="Flight Admin"><SectionHomeLayout title="Flight Admin"><FlightAdminDashboard originalUrl="" title="Flight Admin" /></SectionHomeLayout></TitleSetter>} />

        {/* <Route path="onboarding" element={<Onboarding />} /> */}


        {isFeatureActive("user_settings") && (
          <Route path="settings" element={<Settings />} />
        )}
        {isFeatureActive("tools") && (
          <Route
            path="tools/*"
            element={<Tools originalUrl="" title="Tools" />}
          />
        )}
        <Route
          path="sors/*"
          element={
            <TitleSetter title="Sors">
          <Sors originalUrl={''}
          title={`Service & Operations Report`}/>
          </TitleSetter>
          }
        />
        <Route
          path="aircraft/*"
          element={
            <TitleSetter title="Aircraft">
              <Aircraft originalUrl={""} title={`Aircraft`} />
            </TitleSetter>
          }
        />
        <Route
          path="invoices/*"
          element={
            <TitleSetter title="Invoices">
              <Invoices originalUrl={""} title={`Invoices`} />
            </TitleSetter>
          }
        />
        <Route
          path="journals/*"
          element={
            <TitleSetter title="Journals">
              <Journals originalUrl={""} title={`Journals`} />
            </TitleSetter>
          }
        />
        <Route
          path="finance/journals/*"
          element={
            <TitleSetter title="Journals">
              <Journals originalUrl={""} title={`Journals`} />
            </TitleSetter>
          }
        />
        <Route
          path="batchexports/*"
          element={
            <TitleSetter title="Accounts Exports">
              <BatchExports originalUrl={""} title="Accounts Exports" />
            </TitleSetter>
          }
        />
        <Route
          path="quotes/*"
          element={
            <TitleSetter title="Quotes">
              <Quotes originalUrl={""} title="Quotes" />
            </TitleSetter>
          }
        />
        {/* Finance — /app/financereports → dashboard; /app/financereports/reports. Nav submenu like Inventory. */}
        <Route
          path="financereports/*"
          element={
            <TitleSetter title="Financial Reports">
              <FinancialReports originalUrl="" title="Financial Reports" />
            </TitleSetter>
          }
        />
        <Route
          path="financial-reports/*"
          element={
            <TitleSetter title="Financial Reports">
              <FinancialReports originalUrl="" title="Financial Reports" />
            </TitleSetter>
          }
        />
        <Route
          path="freight/*"
          element={
            <TitleSetter title="Freight">
              <Freight originalUrl={""} title="Freight" />
            </TitleSetter>
          }
        />
        <Route
          path="groups/*"
          element={
            <TitleSetter title="Groups">
              <Groups originalUrl={""} title="Groups" />
            </TitleSetter>
          }
        />
        <Route
          path="currency/*"
          element={
            <TitleSetter title="Currency">
              <Currency originalUrl={""} title="Currency" />
            </TitleSetter>
          }
        />
        <Route
          path="certs/*"
          element={
            <TitleSetter title="Certificates">
              <Certificates originalUrl={""} title="Certificates" />
            </TitleSetter>
          }
        />
        <Route
          path="serviceorders/*"
          element={
            <TitleSetter title="Service Orders">
              <ServiceOrders originalUrl="" title="Service Orders" />
            </TitleSetter>
          }
        />

        <Route
          path="inwardsgoods/*"
          element={
            <TitleSetter title="Inwards Goods">
              <InwardsGoods originalUrl="" title="Inwards Goods" />
            </TitleSetter>
          }
        />
        <Route
          path="inwards-goods/*"
          element={
            <TitleSetter title="Inwards Goods">
              <InwardsGoods originalUrl="" title="Inwards Goods" />
            </TitleSetter>
          }
        />
        <Route
          path="inwards/*"
          element={
            <TitleSetter title="Inwards Goods">
              <InwardsGoods originalUrl="" title="Inwards Goods" />
            </TitleSetter>
          }
        />
        <Route
          path="orders/*"
          element={
            <TitleSetter title="Purchase Orders">
              <Orders originalUrl={""} title="Purchase Orders" />
            </TitleSetter>
          }
        />
        <Route
          path="documents/*"
          element={<Documents originalUrl={""} title={`Documents`} />}
        />
        <Route
          path="components/*"
          element={
            <TitleSetter title="Components">
              <Components originalUrl={""} title={`Components`} />
            </TitleSetter>
          }
        />
        <Route
          path="inspections/*"
          element={
            <TitleSetter title="Inspections">
              <Inspections originalUrl={""} title={`Inspections`} />
            </TitleSetter>
          }
        />
        <Route
          path="inspections_tr/*"
          element={
            <TitleSetter title="Inspections">
              <InspectionsTR originalUrl={""} title={`Inspections`} />
            </TitleSetter>
          }
        />
        <Route
          path="modifications/*"
          element={
            <TitleSetter title="Modifications">
              <Modifications originalUrl={""} title={`Modifications`} />
            </TitleSetter>
          }
        />
        <Route
          path="modification/*"
          element={
            <TitleSetter title="Modifications">
              <Modifications originalUrl={""} title={`Modifications`} />
            </TitleSetter>
          }
        />
        <Route
          path="fleet/*"
          element={
            <TitleSetter title="Fleet Report">
              <Fleet originalUrl={""} title={`Fleet Report`} />
            </TitleSetter>
          }
        />
        <Route
          path="weather/*"
          element={
            <TitleSetter title="Weather">
              <Weather originalUrl="" title="Weather" />
            </TitleSetter>
          }
        />
        <Route
          path="pilots/weather/*"
          element={
            <TitleSetter title="Weather">
              <Weather originalUrl="" title="Weather" />
            </TitleSetter>
          }
        />
        <Route
          path="systemreports/*"
          element={
            <TitleSetter title="System Reports">
              <Reports />
            </TitleSetter>
          }
        />
        <Route
          path="partsorders/*"
          element={
            <TitleSetter title="Parts Orders">
              <PartsOrders  originalUrl={""} title={`Parts Orders`} />
            </TitleSetter>
          }
        />
        <Route
          path="parts-orders/*"
          element={
            <TitleSetter title="Parts Orders">
              <PartsOrders originalUrl={""} title={`Parts Orders`} />
            </TitleSetter>
          }
        />
        <Route
          path="timesheets/*"
          element={
            <TitleSetter title="Time Sheets">
              <Timesheets originalUrl={""} title={`Time Sheets`} />
            </TitleSetter>
          }
        />
        <Route
          path="time-sheets/*"
          element={
            <TitleSetter title="Time Sheets">
              <Timesheets originalUrl={""} title={`Time Sheets`} />
            </TitleSetter>
          }
        />
        <Route
          path="production/*"
          element={
            <TitleSetter title="Production">
              <Production originalUrl={""} title={`Production`} />
            </TitleSetter>
          }
        />
        <Route
          path="production-planning/*"
          element={
            <TitleSetter title="Production">
              <Production originalUrl={""} title={`Production`} />
            </TitleSetter>
          }
        />
        <Route
          path="parts-requisition/*"
          element={
            <TitleSetter title="Parts Requisition">
              <PartsRequisition originalUrl={""} title={`Parts Requisition`} />
            </TitleSetter>
          }
        />
        <Route
          path="partsrequisitions/*"
          element={
            <TitleSetter title="Parts Requisition">
              <PartsRequisition originalUrl={""} title={`Parts Requisition`} />
            </TitleSetter>
          }
        />
        <Route path="duedate/*" element = {<DueDate originalUrl="" title="Due Date Control"/>}/>
        <Route path="due_date_control/*" element = {<DueDate originalUrl="" title="Due Date Control"/>}/>
        <Route path="due-date-control/*" element = {<DueDate originalUrl="" title="Due Date Control"/>}/>
        <Route path="whiteboard/*" element = {<DueDate originalUrl="" title="Due Date Control"/>}/>
        <Route
          path="salesorders/*"
          element={
            <TitleSetter title="Sales Orders">
              <SalesOrder originalUrl="" title="Sales Orders" />
            </TitleSetter>
          }
        />
        <Route
          path="workorders/*"
          element={
            <TitleSetter title="Work Orders">
              <WorkOrder originalUrl="" title="Work Orders" />
            </TitleSetter>
          }
        />
        <Route
          path="work-orders/*"
          element={
            <TitleSetter title="Work Orders">
              <WorkOrder originalUrl="" title="Work Orders" />
            </TitleSetter>
          }
        />
        <Route
          path="jobs/*"
          element={
            <TitleSetter title="Jobs">
              <Jobs originalUrl="" title="Jobs" />
            </TitleSetter>
          }
        />
        {isFeatureActive("module_stationery") && (
          <Route
            path="reports-and-forms/*"
            element={
              <TitleSetter title="Reports and Forms">
                <ReportsAndForms originalUrl="" title="Reports and Forms" />
              </TitleSetter>
            }
          />
        )}
        <Route path="actionregister/*" element = {<ActionRegister originalUrl="" title="Action Register"/>}/>
        <Route path="rmas/*" element = {<Rmas originalUrl="" title="Return Material Authorizations"/>}/>
        <Route path="followup/*" element = {<FollowUp originalUrl="" title="FollowUps"/>}/>
        <Route path="maintenance-log/*" element={<TitleSetter title="Maintenance Log"><MaintenanceLog originalUrl="" title="Maintenance Log" /></TitleSetter>} />
        <Route path="maintenance_log/*" element={<TitleSetter title="Maintenance Log"><MaintenanceLog originalUrl="" title="Maintenance Log" /></TitleSetter>} />
        <Route path="maintenancelog/*" element={<TitleSetter title="Maintenance Log"><MaintenanceLog originalUrl="" title="Maintenance Log" /></TitleSetter>} />
        <Route path="role-equipment/*" element={<TitleSetter title="Role Equipment"><RoleEquipment originalUrl="" title="Role Equipment" /></TitleSetter>} />
        <Route path="role_equipment/*" element={<TitleSetter title="Role Equipment"><RoleEquipment originalUrl="" title="Role Equipment" /></TitleSetter>} />
        <Route path="roleequipment/*" element={<TitleSetter title="Role Equipment"><RoleEquipment originalUrl="" title="Role Equipment" /></TitleSetter>} />
        <Route path="tech-log/*" element={<TitleSetter title="Tech Log"><TechLog originalUrl="" title="Tech Log" /></TitleSetter>} />
        <Route path="tech_log/*" element={<TitleSetter title="Tech Log"><TechLog originalUrl="" title="Tech Log" /></TitleSetter>} />
        <Route path="techlog/*" element={<TitleSetter title="Tech Log"><TechLog originalUrl="" title="Tech Log" /></TitleSetter>} />
        <Route path="trend/*" element={<TitleSetter title="Engine Trend"><Trend originalUrl="" title="Engine Trend" /></TitleSetter>} />
        <Route path="engine-trend/*" element={<TitleSetter title="Engine Trend"><Trend originalUrl="" title="Engine Trend" /></TitleSetter>} />
        <Route path="engine_trend/*" element={<TitleSetter title="Engine Trend"><Trend originalUrl="" title="Engine Trend" /></TitleSetter>} />
        <Route path="module-placeholder/Trend/*" element={<TitleSetter title="Engine Trend"><Trend originalUrl="" title="Engine Trend" /></TitleSetter>} />
        <Route path="xero/callback" element={<XeroCallback />} />
        <Route path="xero/*" element={<TitleSetter title="Xero"><XeroSettings /></TitleSetter>} />
        <Route path="finance/xero/*" element={<TitleSetter title="Xero"><XeroSettings /></TitleSetter>} />
        <Route path="transpond/*" element={<TitleSetter title="Transpond"><Transpond originalUrl="" title="Transpond" /></TitleSetter>} />
        <Route path="microsoft365/*" element={<TitleSetter title="Microsoft 365"><Microsoft365 /></TitleSetter>} />
        <Route path="sage300/*" element={<TitleSetter title="Sage 300"><Sage300 /></TitleSetter>} />
        <Route path="laserfiche/*" element={<TitleSetter title="Laserfiche"><Laserfiche /></TitleSetter>} />
        <Route path="landing/:sectionName/*" element={<SectionLanding />} />

        <Route path="dash/:id" element = {<MetaBaseDashboard />}/>
        <Route path="metabase/*" element={<TitleSetter title="Metabase Reporting"><MetabaseRoutes /></TitleSetter>} />
        <Route path="metabase-reporting/*" element={<TitleSetter title="Metabase Reporting"><MetabaseRoutes /></TitleSetter>} />
        <Route path="metabase_reports/*" element={<TitleSetter title="Metabase Reporting"><MetabaseRoutes /></TitleSetter>} />
        <Route path="admin/metabase/*" element={<TitleSetter title="Metabase Reporting"><MetabaseRoutes /></TitleSetter>} />
        <Route path="system-config/*" element={<TitleSetter title="System Config"><SystemConfig /></TitleSetter>} />
        <Route path="system_config/*" element={<TitleSetter title="System Config"><SystemConfig /></TitleSetter>} />
        <Route path="systemconfig/*" element={<TitleSetter title="System Config"><SystemConfig /></TitleSetter>} />
        <Route path="types/*" element={<TitleSetter title="System Config"><SystemConfig /></TitleSetter>} />
        <Route path="reports/*" element={<TitleSetter title="Reports"><SystemReports /></TitleSetter>} />
        <Route path="system-reports/*" element={<TitleSetter title="Reports"><SystemReports /></TitleSetter>} />
        <Route path="system_reports/*" element={<TitleSetter title="Reports"><SystemReports /></TitleSetter>} />
        <Route path="personnel/*" element={<TitleSetter title="Personnel"><Personnel originalUrl="" title="Personnel" /></TitleSetter>} />
        <Route path="history/*" element={<TitleSetter title="History"><History originalUrl="" title="History" /></TitleSetter>} />
        <Route path="tools/*" element={<TitleSetter title="Tools"><Tools originalUrl="" title="Tools" /></TitleSetter>} />
        <Route path="toolrequests/*" element={<TitleSetter title="Tool Requests"><ToolRequests originalUrl="" title="Tool Requests" /></TitleSetter>} />
        <Route path="tasks/*" element={<TitleSetter title="Tasks"><Tasks originalUrl="" title="Tasks" /></TitleSetter>} />
        {/* The Log Book register + entry record (Aircraft Patch 5). Static as well as nav-driven
            (`lbe`), because the aircraft and work order Log Book tabs open /lbe/:id whether or
            not the tenant's nav lists Log Book; the API owns who may write. */}
        <Route path="lbe/*" element={<TitleSetter title="Log Book"><AircraftServices originalUrl="" title="Log Book" /></TitleSetter>} />
        <Route
          path="manifest/*"
          element={
            <TitleSetter title="Pax Manifest">
              <Manifest originalUrl="" title="Pax Manifest" />
            </TitleSetter>
          }
        />
        <Route path="AddressBook/*" element={<TitleSetter title="Address Book"><Companies originalUrl="" title="Address Book" /></TitleSetter>} />
        <Route path="companies/*" element={<TitleSetter title="Address Book"><Companies originalUrl="" title="Address Book" /></TitleSetter>} />
        <Route path="supplier_invoices/*" element={<TitleSetter title="Supplier Invoices"><SupplierInvoices originalUrl="" title="Supplier Invoices" /></TitleSetter>} />
        <Route path="supplier-invoices/*" element={<TitleSetter title="Supplier Invoices"><SupplierInvoices originalUrl="" title="Supplier Invoices" /></TitleSetter>} />
        <Route path="customer_quotes/*" element={<TitleSetter title="Customer Quotes"><Quotes originalUrl="" title="Customer Quotes" /></TitleSetter>} />
        <Route path="customer-quotes/*" element={<TitleSetter title="Customer Quotes"><Quotes originalUrl="" title="Customer Quotes" /></TitleSetter>} />
        {nav && nav.length > 0 && <Route path="*" element={<NotFound />} />}
        {nav.map((navItem, index) =>
          navItem.children.map((menuItem, index) => {
            if (menuItem.to) {
              const featureName = menuItem.remote_menu_key
                ? menuItem.remote_menu_key
                : menuItem.function;
              const feature = getFeature(featureName);
              if (feature) {
                let FeatureComponent = feature.component;
                if (smallScreen && feature.responsiveComponent) {
                  FeatureComponent = feature.responsiveComponent;
                }

                return (
                  <Route
                    path={menuItem.to.split("?")[0] + "/*"}
                    element={
                      <TitleSetter title={menuItem.name}>
                        <FeatureComponent
                          originalUrl={menuItem.url}
                          title={menuItem.name}
                        />
                      </TitleSetter>
                    }
                  />
                );
              }
            }
            return null;
          })
        )}
      </Route>
    </SentryRoutes>
    </PageTitleProvider>
  );
};

export enum UsernameAttributes {
  EMAIL = "email",
  PHONE_NUMBER = "phone_number",
  USERNAME = "username",
}

const CustomTheme = {
  container: {
    height: "100%",
  },
};

const StyledMaterialDesignContent = styled(MaterialDesignContent)(
  ({ theme }) => ({
    "&.notistack-MuiContent-success": {
      backgroundColor: theme.palette.primary.main,
    },
  })
);

const NotistackProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  // add action to all snackbars
  const notistackRef = createRef<any>();

  const onClickDismiss = (key) => () => {
    notistackRef.current!.closeSnackbar(key);
  };

  return (
    <SnackbarProvider
      maxSnack={10}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      preventDuplicate
      ref={notistackRef}
      action={(key) => (
        <Button onClick={onClickDismiss(key)} sx={{ color: "white" }}>
          Dismiss
        </Button>
      )}
      children={children}
      Components={{
        success: StyledMaterialDesignContent,
      }}
    />
  );
};

class AppWithAuth extends Component {
  componentDidMount() {
    hideLoader();
  }

  render() {
    // Public signer links (document execution, version-36): no sign-in; the token is in the URL fragment.
    if (window.location.pathname.replace(/\/+$/, "") === "/app/sign") {
      return (
        <NotistackProvider>
          <SignPage />
        </NotistackProvider>
      );
    }

    const isLocalDev =
      window.location.hostname.includes("localhost") ||
      window.location.hostname.includes("127.0.0.1") ||
      process.env.REACT_APP_ENV === "local";

    const gate =
      getAuthProvider() === "gcip" ? (
        <GcipGate>
          <App authState="signedIn" />
        </GcipGate>
      ) : (
        /* @ts-ignore */
        <Authenticator
          authState="signIn"
          hide={[SignIn, Loading, Greetings]}
          usernameAttributes={UsernameAttributes.EMAIL}
          theme={CustomTheme}
        >
          <CustomSignIn />
          <CustomLoading />
          <App />
        </Authenticator>
      );

    return (
      <NotistackProvider>
        {isLocalDev ? <App authState="signedIn" /> : gate}
      </NotistackProvider>
    );
  }
}

export { App };
export default AppWithAuth;
